/**
 * videoEncode — turn a frame-by-frame drawing routine into a downloaded video.
 *
 * WebCodecs (H.264) + mp4-muxer where the browser has them: the frames encode as
 * fast as the machine manages, and the tab needn't stay in front. Elsewhere the
 * canvas is recorded in real time with MediaRecorder (MP4 in Safari, WebM in the rest).
 */

import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

export interface EncodeOpts {
  fps: number;
  /** Number of frames. */
  total: number;
  /** Draw frame n (0-based) onto the canvas. */
  drawFrame: (n: number) => void;
  /** Download name without extension. */
  name: string;
  onProgress?: (fraction: number) => void;
}

const MP4_CONFIG = (w: number, h: number, fps: number): VideoEncoderConfig => ({
  codec: 'avc1.640028', // H.264 High 4.0 — 1920×1080 at 30 fps, and its portrait twins
  width: w,
  height: h,
  bitrate: 10_000_000,
  framerate: fps,
  avc: { format: 'avc' },
});

async function canEncodeMp4(w: number, h: number, fps: number): Promise<boolean> {
  if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') return false;
  try {
    const res = await VideoEncoder.isConfigSupported(MP4_CONFIG(w, h, fps));
    return !!res.supported;
  } catch {
    return false;
  }
}

function save(blob: Blob, filename: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Encode `total` frames drawn onto `work` and trigger the download. Resolves to the extension used. */
export async function encodeCanvasVideo(work: HTMLCanvasElement, opts: EncodeOpts): Promise<'mp4' | 'webm'> {
  const { fps, total, drawFrame, name, onProgress } = opts;
  const W = work.width;
  const H = work.height;

  // WebCodecs → MP4, as fast as the machine encodes
  if (await canEncodeMp4(W, H, fps)) {
    const target = new ArrayBufferTarget();
    const muxer = new Muxer({
      target,
      video: { codec: 'avc', width: W, height: H },
      fastStart: 'in-memory',
      firstTimestampBehavior: 'offset',
    });
    let failed: Error | null = null;
    const encoder = new VideoEncoder({
      output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
      error: (e) => {
        failed = e;
      },
    });
    encoder.configure(MP4_CONFIG(W, H, fps));
    const us = 1_000_000 / fps;
    for (let n = 0; n < total; n++) {
      if (failed) throw failed;
      drawFrame(n);
      const frame = new VideoFrame(work, { timestamp: Math.round(n * us), duration: Math.round(us) });
      encoder.encode(frame, { keyFrame: n % (fps * 2) === 0 });
      frame.close();
      // Backpressure through the encoder itself — a timer-based yield is throttled
      // to once a second in a background tab, which turned a 10s render into
      // minutes. flush() simply waits for the queue to drain, tab in front or not.
      if (encoder.encodeQueueSize > 30) await encoder.flush();
      if (n % 15 === 0) onProgress?.(n / total);
    }
    await encoder.flush();
    if (failed) throw failed;
    muxer.finalize();
    save(new Blob([target.buffer], { type: 'video/mp4' }), `${name}.mp4`);
    onProgress?.(1);
    return 'mp4';
  }

  // MediaRecorder, in real time
  if (typeof MediaRecorder === 'undefined') throw new Error('This browser cannot encode video');
  const mime =
    ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm'].find((m) =>
      MediaRecorder.isTypeSupported(m)
    ) || '';
  drawFrame(0);
  const stream = work.captureStream(fps);
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 10_000_000 } : undefined);
  const chunks: BlobPart[] = [];
  rec.ondataavailable = (e) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };
  const stopped = new Promise<void>((resolve) => {
    rec.onstop = () => resolve();
  });
  rec.start();
  const start = performance.now();
  await new Promise<void>((resolve) => {
    const tick = () => {
      const n = Math.floor(((performance.now() - start) / 1000) * fps);
      if (n >= total) {
        drawFrame(total - 1);
        resolve();
        return;
      }
      drawFrame(n);
      onProgress?.(n / total);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
  await new Promise((r) => setTimeout(r, 350));
  rec.stop();
  await stopped;
  const ext = mime.startsWith('video/mp4') ? 'mp4' : 'webm';
  save(new Blob(chunks, { type: mime || 'video/webm' }), `${name}.${ext}`);
  onProgress?.(1);
  return ext;
}
